import { useCallback, useState } from "react";
import type { TextSelectionHandler } from "@libertify/experience-viewer";
import type { PendingSelection, StandoutSelection } from "./types";

/**
 * The latest text selected in the PDF pane, with where it sits on screen.
 * The viewer can report a partial selection mid-drag, so each call replaces
 * the previous one.
 */
export const useStandoutSelection = (
  activeDocumentId: string
): StandoutSelection => {
  const [pending, setPending] = useState<PendingSelection | null>(null);

  // A selection made before a document switch no longer points at what's
  // shown. Drop it rather than hide it, so switching back can't revive it.
  if (pending && pending.documentId !== activeDocumentId) setPending(null);

  const onTextSelection = useCallback<TextSelectionHandler>(
    (zone, clearSelection) => {
      // Read before the caller clears the browser selection.
      const range = window.getSelection()?.rangeCount
        ? window.getSelection()?.getRangeAt(0)
        : undefined;
      const rect = range?.getBoundingClientRect();
      if (!rect) return;
      setPending({
        zone,
        documentId: activeDocumentId,
        anchor: { top: rect.bottom, left: rect.left },
        clearSelection,
      });
    },
    [activeDocumentId]
  );

  const close = useCallback(() => setPending(null), []);

  const dismiss = useCallback(() => {
    pending?.clearSelection();
    setPending(null);
  }, [pending]);

  return {
    pending,
    onTextSelection,
    close,
    dismiss,
  };
};
